import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Table, ButtonGroup, Row, Col, Alert, OverlayTrigger, Popover, Card } from "react-bootstrap";
import { LifePart } from "../../state/LifePart";
import Icon from '../Icon';
import {
    faPlus as add,
    faTrash as trash,
    faArrowUp as arrowUp,
    faArrowDown as arrowDown,
    faMinus as minus,
    faPlus as plus,
    faCircleQuestion as questionIcon,
    faCircleCheck as checkCircle,
    faCircle as circle,
} from '@fortawesome/free-solid-svg-icons';
import LifePartSimpleModal from "./lifePartSimpleModal";
import ColorPicker from "./ColorPicker";
import Chart from "../chart";
import { useData } from "@/storage/accessors";
import { useScreenSize } from "@/contexts/screenSizeContext";
import { useNavigate } from "react-router";
import useWheelId from "@/hooks/useWheelId";
import { format, startOfWeek, endOfWeek, addWeeks, getDay } from "date-fns";
import "@/globals.css";
import { RoleEfficiencySnapshot } from "../../state/RoleEfficiencySnapshot";
import { useReadonly } from "@/contexts/readonlyContext";

/**
 * Интерфейс критерия эффективности.
 */
export interface EfficiencyCriterion {
    name: string;
    met: boolean;
    description: string;
    action: string;
}

/**
 * Результат вычисления эффективности роли.
 */
export interface RoleEfficiencyResult {
    score: number;
    criteria: EfficiencyCriterion[];
}

/**
 * Вычисляет эффективность одной роли (score 0-8) и детальные критерии.
 * Используется как единая точка расчёта и в LifePartIndicator, и в saveEfficiencySnapshot.
 */
export function computeRoleEfficiency(
    lifePart: LifePart,
    tasks: Record<string, any>,
    plans: Record<string, any>,
    trackers: Record<string, any>,
): RoleEfficiencyResult {
    let score = 1; // Базовое минимальное значение
    const criteria: EfficiencyCriterion[] = [
        { name: "Базовое значение", met: true, description: "Запись создана", action: "" },
        { name: "Есть описание", met: false, description: "Описание заполнено", action: "Добавьте описание" },
        { name: "Есть цели", met: false, description: "Определены цели", action: "Определите цели" },
        { name: "Есть задачи в незавершённых шагах", met: false, description: "Есть задачи в незавершённых шагах", action: "Добавьте задачи в будущие шаги" },
        { name: "Есть трекеры", met: false, description: "Есть трекеры для отслеживания", action: "Создайте трекер для этой роли" },
        { name: "Задачи на неделю", met: false, description: "", action: "" },
        { name: "Есть задачи в каждом из оставшихся шагов", met: false, description: "Все оставшиеся шаги имеют задачи", action: "Добавьте задачи в каждый оставшийся шаг" },
        { name: "Выполненные задачи на неделе", met: false, description: "Есть выполненные задачи на этой неделе", action: "Сделайте запланированные задачи" },
    ];

    // 2. Если есть описание +1
    if (lifePart.description && lifePart.description.trim().length > 0) {
        score++;
        criteria[1].met = true;
    }

    // 3. Если есть критерии +1
    if (lifePart.successSigns && lifePart.successSigns.length > 0) {
        score++;
        criteria[2].met = true;
    }

    // 4. Если есть задачи хотя бы в одном из шагов (при этом этот шаг еще не достигнут)
    const currentStartpoint = lifePart.startpoint || 1;
    const tasksByPoint: Record<number, any[]> = {};
    Object.values(tasks).forEach((task: any) => {
        if (task.lifepart === lifePart.uuid && task.forPoint !== null && task.forPoint !== undefined) {
            const point = task.forPoint;
            if (!tasksByPoint[point]) {
                tasksByPoint[point] = [];
            }
            tasksByPoint[point].push(task);
        }
    });

    const hasTasksInIncompleteSteps = Object.keys(tasksByPoint).some((pointStr) => {
        const point = parseInt(pointStr, 10);
        return point > currentStartpoint && tasksByPoint[point].length > 0;
    });
    if (hasTasksInIncompleteSteps) {
        score++;
        criteria[3].met = true;
    }

    // 5. Если есть трекеры +1
    const hasTrackers = Object.values(trackers).some((t: any) => t.lifepart === lifePart.uuid);
    if (hasTrackers) {
        score++;
        criteria[4].met = true;
    }

    // 6. Если задачи из шагов запланированы на текущую неделю +1
    const today = new Date();
    const dayOfWeek = getDay(today);
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // Для критерия 6 (запланированные задачи): в сб-вс смотрим на следующую неделю
    const plannedWeekStart = isWeekend
        ? startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 })
        : startOfWeek(today, { weekStartsOn: 1 });
    const plannedWeekEnd = isWeekend
        ? endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 })
        : endOfWeek(today, { weekStartsOn: 1 });
    const plannedWeekStartStr = format(plannedWeekStart, 'yyyy-MM-dd');
    const plannedWeekEndStr = format(plannedWeekEnd, 'yyyy-MM-dd');

    // Для критерия 8 (выполненные задачи): всегда смотрим на текущую неделю
    const completedWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    const completedWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const completedWeekStartStr = format(completedWeekStart, 'yyyy-MM-dd');
    const completedWeekEndStr = format(completedWeekEnd, 'yyyy-MM-dd');

    // Критерий 5 — текст зависит от того, текущая или следующая неделя
    criteria[5].description = isWeekend
        ? "Задачи запланированы на следующую неделю"
        : "Задачи запланированы на текущую неделю";
    criteria[5].action = isWeekend
        ? "Запланируйте задачи на следующую неделю"
        : "Запланируйте задачи на текущую неделю";

    const taskUuidsWithPoints = Object.values(tasks)
        .filter((t: any) => t.lifepart === lifePart.uuid && t.forPoint !== null && t.forPoint !== undefined)
        .map((t: any) => t.uuid);

    const hasPlansThisWeek = Object.values(plans).some((plan: any) => {
        return (
            taskUuidsWithPoints.includes(plan.task) &&
            plan.date >= plannedWeekStartStr &&
            plan.date <= plannedWeekEndStr
        );
    });
    if (hasPlansThisWeek) {
        score++;
        criteria[5].met = true;
    }

    // 7. Если есть задачи в каждом из оставшихся шагов +1
    const remainingSteps = [];
    for (let i = currentStartpoint + 1; i <= 10; i++) {
        remainingSteps.push(i);
    }

    const hasTasksInAllRemainingSteps = remainingSteps.length > 0 && remainingSteps.every((step) => {
        return tasksByPoint[step] && tasksByPoint[step].length > 0;
    });
    if (hasTasksInAllRemainingSteps) {
        score++;
        criteria[6].met = true;
    }

    // 8. Если есть выполненные DayPlan по задачам из шагов в текущей неделе +1
    const hasCompletedPlansThisWeek = Object.values(plans).some((plan: any) => {
        return (
            taskUuidsWithPoints.includes(plan.task) &&
            plan.isDone &&
            plan.date >= completedWeekStartStr &&
            plan.date <= completedWeekEndStr
        );
    });
    if (hasCompletedPlansThisWeek) {
        score++;
        criteria[7].met = true;
    }

    return { score, criteria };
}

/**
 * Вычисляет эффективность всех ролей и сохраняет/обновляет снепшот на сегодня.
 */
export function saveEfficiencySnapshot(
    wheelId: string,
    roleEfficiencySnapshots: { objects: Record<string, RoleEfficiencySnapshot>, save: (uuid: string, update: Partial<RoleEfficiencySnapshot>) => void, create: (obj: RoleEfficiencySnapshot) => void },
    roles: LifePart[],
    tasks: Record<string, any>,
    plans: Record<string, any>,
    trackers: Record<string, any>,
) {
    const scores: Record<string, number> = {};
    roles.forEach((part) => {
        scores[part.uuid] = computeRoleEfficiency(part, tasks, plans, trackers).score;
    });

    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = Object.values(roleEfficiencySnapshots.objects).find(r => r.date === today);
    if (existing) {
        roleEfficiencySnapshots.save(existing.uuid, { scores, timestamp: Date.now() });
    } else {
        roleEfficiencySnapshots.create({
            uuid: crypto.randomUUID(),
            wheelId,
            timestamp: Date.now(),
            date: today,
            scores,
        });
    }
}

/**
 * Индикатор готовности LifePart из 8 кружков
 * Правила расчёта:
 * 1. Минимальное значение = 1 (базовое)
 * 2. Если есть описание +1
 * 3. Если есть критерии +1
 * 4. Если есть задачи хотя бы в одном из шагов (при этом этот шаг еще не достигнут) +1
 * 5. Если есть трекеры +1
 * 6. Если задачи из шагов запланированы на текущую неделю +1
 * 7. Если есть задачи в каждом из оставшихся шагов +1
 * 8. Если есть выполенные DayPlan по задачам из шагов в текущей неделе +1
 */
function LifePartIndicator({
    lifePart,
    tasks,
    plans,
    trackers,
}: {
    lifePart: LifePart;
    tasks: Record<string, any>;
    plans: Record<string, any>;
    trackers: Record<string, any>;
}) {
    // Используем единую точку расчёта
    const { score, criteria } = useMemo(
        () => computeRoleEfficiency(lifePart, tasks, plans, trackers),
        [lifePart, tasks, plans, trackers],
    );

    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <div className="d-flex align-items-center gap-1 mt-1" style={{ cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>
                {Array.from({ length: 8 }, (_, i) => i).map((index) => (
                    <span
                        key={index}
                        style={{
                            width: 14,
                            height: 14,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            borderRadius: '50%',
                            border: index < score ? 'none' : `2px solid #198754`,
                            backgroundColor: index < score ? '#198754' : 'transparent',
                        }}
                    >
                    </span>
                ))}
                {score === 8 && (
                    <span
                        style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: lifePart.color || '#198754',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 4,
                            animation: 'lp-checkmark-pulse 0.5s ease-in-out',
                        }}
                    >
                        <Icon icon={checkCircle} style={{ color: 'white', fontSize: '12px' }} />
                    </span>
                )}
            </div>

            <div
                className={`efficiency-checklist ${isOpen ? 'open' : ''}`}
                style={{
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease-out, opacity 0.2s ease-out, margin 0.3s ease-out',
                    maxHeight: isOpen ? '500px' : '0',
                    opacity: isOpen ? 1 : 0,
                    margin: isOpen ? '0.5rem 0 0 0' : '0',
                    maxWidth: '100%',
                }}
            >
                <div className="d-flex flex-column gap-1 p-2" style={{ fontSize: '0.8rem', minWidth: 0 }}>
                    <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                        Эффективность: {score} из 8
                    </div>
                    {criteria.map((criterion, index) => (
                        <div key={index} className="d-flex align-items-start gap-2">
                            <span style={{ minWidth: 16, marginTop: 2 }}>
                                {criterion.met ? (
                                    <span style={{ color: '#198754' }}>✓</span>
                                ) : (
                                    <span style={{ color: '#dc3545' }}>✗</span>
                                )}
                            </span>
                            <div>
                                {criterion.met ? (
                                    <div>{criterion.description}</div>
                                ) : (
                                    <div>{criterion.action}</div>
                                )}
                            </div>
                        </div>
                    ))}
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="mt-1"
                        style={{ fontSize: '0.75rem' }}
                    >
                        Закрыть
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function LifeParts() {
    const navigate = useNavigate();
    const wheelId = useWheelId();
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const { readonly } = useReadonly();
    const [newLifePart, setNewLifepart] = useState(false);
    const toggleNew = () => setNewLifepart(false);

    const { roles, tasks, plans, trackers, roleEfficiencySnapshots } = useData();
    const sortedRoles: LifePart[] = [...Object.values(roles.objects)].sort((a, b) => (a.order || 0) - (b.order || 0));
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const hasPoints = sortedRoles.filter((p) => p.startpoint != 1).length > 0;

    const handleOpenNewLifePartModal = useCallback(() => {
        setNewLifepart(true);
    }, []);

    const plusEventListener = useCallback((e: any) => {
        if (e.key === "+") {
            handleOpenNewLifePartModal();
        }
    }, [handleOpenNewLifePartModal]);

    useEffect(() => {
        document.addEventListener("keypress", plusEventListener);
        return () => document.removeEventListener("keypress", plusEventListener);
    }, [plusEventListener]);

    const handleColorChange = (part: LifePart, color: string) => {
        if (readonly) return;
        roles.save(part.uuid, { color });
    };

    const handleDeleteLifePart = (part: LifePart) => {
        if (readonly) return;
        if (confirm(`Удалить ${part.name}?`)) {
            roles.delete(part.uuid);
        }
    };

    const handleMoveUp = (part: LifePart) => {
        if (readonly) return;
        const index = sortedRoles.findIndex(p => p.uuid === part.uuid);
        if (index > 0) {
            const currentOrder = part.order;
            const prevOrder = sortedRoles[index - 1].order;
            roles.save(part.uuid, { order: prevOrder });
            roles.save(sortedRoles[index - 1].uuid, { order: currentOrder });
        }
    };

    const handleMoveDown = (part: LifePart) => {
        if (readonly) return;
        const index = sortedRoles.findIndex(p => p.uuid === part.uuid);
        if (index < sortedRoles.length - 1) {
            const currentOrder = part.order;
            const nextOrder = sortedRoles[index + 1].order;
            roles.save(part.uuid, { order: nextOrder });
            roles.save(sortedRoles[index + 1].uuid, { order: currentOrder });
        }
    };

    const handleRatingChange = (part: LifePart, delta: number) => {
        if (readonly) return;
        const currentRating = part.startpoint || 0;
        const newRating = Math.max(0, Math.min(10, currentRating + delta));
        roles.save(part.uuid, { startpoint: newRating });
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        // Делаем перетаскиваемый элемент более прозрачным
        e.currentTarget.classList.add("dragging");
    };

    const handleDragEnd = (e: React.DragEvent) => {
        e.currentTarget.classList.remove("dragging");
        setDraggedIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        if (readonly) return;
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const draggedRole = sortedRoles[draggedIndex];
        const droppedRole = sortedRoles[dropIndex];

        // Обновляем порядок: draggedRole получает порядок droppedRole
        const newOrder = droppedRole.order;

        // Сдвигаем порядок для всех элементов между dragged и dropped
        const rolesToReorder: Array<{ uuid: string; order: number }> = [];

        if (draggedIndex < dropIndex) {
            // Перетаскиваем вниз
            for (let i = draggedIndex + 1; i <= dropIndex; i++) {
                rolesToReorder.push({ uuid: sortedRoles[i].uuid, order: (sortedRoles[i].order || 0) - 1 });
            }
        } else {
            // Перетаскиваем вверх
            for (let i = dropIndex; i < draggedIndex; i++) {
                rolesToReorder.push({ uuid: sortedRoles[i].uuid, order: (sortedRoles[i].order || 0) + 1 });
            }
        }

        // Сохраняем новый порядок для draggedRole
        roles.save(draggedRole.uuid, { order: newOrder });
        // Сохраняем новый порядок для остальных
        rolesToReorder.forEach(({ uuid, order }) => {
            roles.save(uuid, { order });
        });

        setDraggedIndex(null);
    };

    const RoleCard = ({ part, index }: { part: LifePart; index: number }) => (
        <Card
            key={part.uuid}
            className="mb-4"
            style={{ overflow: 'hidden' }}
        >
            <Card.Body style={{ overflow: 'hidden' }}>
                <div className="d-flex align-items-center">

                    <span className="me-2 text-muted" style={{ minWidth: 24 }}>{index + 1}</span>
                    <span className="fw-bold" style={{ cursor: 'pointer' }} onClick={() => navigate(`/${wheelId}/roles/${part.uuid}`)}>{part.name}</span>
                </div>
                <div className="mt-1">
                    <LifePartIndicator
                        lifePart={part}
                        tasks={tasks.objects}
                        plans={plans.objects}
                        trackers={trackers.objects}
                    />
                </div>
            </Card.Body>
            <Card.Footer>
                <div className="d-flex justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <ColorPicker
                            value={part.color || '#000000'}
                            onClick={e => e.stopPropagation()}
                            onChange={(color) => handleColorChange(part, color)}
                            disabled={readonly}
                        />
                        <ButtonGroup size="sm">
                            <Button onClick={(e) => { handleMoveUp(part); e.stopPropagation() }} variant="outline-secondary" disabled={readonly}>
                                <Icon icon={arrowUp} />
                            </Button>
                            <Button onClick={(e) => { handleMoveDown(part); e.stopPropagation() }} variant="outline-secondary" disabled={readonly}>
                                <Icon icon={arrowDown} />
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <ButtonGroup size="sm">
                            <Button
                                onClick={(e) => { handleRatingChange(part, -1); e.stopPropagation() }}
                                variant="outline-secondary"
                                disabled={readonly || (part.startpoint || 0) <= 0}
                            >
                                <Icon icon={minus} />
                            </Button>
                            <span className="d-flex align-items-center px-2" style={{ minWidth: 32 }}>
                                {part.startpoint || 0}
                            </span>
                            <Button
                                onClick={(e) => { handleRatingChange(part, 1); e.stopPropagation() }}
                                variant="outline-secondary"
                                disabled={readonly || (part.startpoint || 0) >= 10}
                            >
                                <Icon icon={plus} />
                            </Button>
                        </ButtonGroup>
                        <Button size="sm" variant="danger" disabled={readonly} onClick={(e) => { e.stopPropagation(); handleDeleteLifePart(part) }}>
                            <Icon icon={trash} />
                        </Button>
                    </div>

                </div>
            </Card.Footer>
        </Card>
    );
    // TODO: это можно удалить без раздумий после старта продаж
    useEffect(() => {
        sortedRoles.map((sr, index) => {
            if (sr.order === undefined) {
                roles.save(sr.uuid, { order: index })
            }
        })
    }, [sortedRoles])



    return (<Row className="d-flex align-items-center">
        {isMobile ? (
            <>
                <Col xs={12} className="mb-3 text-center">
                    <h3>Баланс</h3>
                </Col>
                <Col xs={12} className="mb-3">
                    <Chart
                        allMaxValues={!Boolean(hasPoints)}
                        showLabels={false}
                        onClick={(index) => navigate(`/${wheelId}/roles/${sortedRoles[index].uuid}`)}
                        roles={sortedRoles.filter((p): p is LifePart => p !== undefined)}
                    />

                </Col>
                <Col xs={12}>
                    {sortedRoles.map((part, index) => (
                        <RoleCard key={part.uuid} part={part} index={index} />
                    ))}
                    {sortedRoles.length > 12 && <Alert variant="warning"><strong>Оптимальное количество частей: от 6 до 12</strong></Alert>}
                    {newLifePart && <LifePartSimpleModal toggle={toggleNew} popup={newLifePart} />}
                    {sortedRoles.length === 0 && <Alert variant="info">Вы еще не начали составлять Баланс</Alert>}
                    <Button variant="outline-success" className="w-100 mt-3" onClick={handleOpenNewLifePartModal} disabled={readonly}>
                        <Icon icon={add} className="me-2" />Добавить
                    </Button>
                </Col>
            </>
        ) : (
            <>
                <Col lg={7}>
                    {sortedRoles.length === 0 && <Alert variant="info">Вы еще не начали составлять Баланс</Alert>}
                    {sortedRoles.length > 0 && <Table hover>
                        <thead>
                            <tr >
                                <th style={{ maxWidth: 36, minWidth: 36 }}><h5 className="text-secondary">#</h5></th>
                                <th><h5 className="text-secondary">Название</h5></th>
                                <th><h5 className="text-secondary">Цвет</h5></th>
                                <th>
                                    <h5 className="text-secondary d-inline-flex align-items-center">
                                        Оценка
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Popover id="rating-popover">
                                                    <Popover.Body className="p-2">
                                                        Ваша текущая удовлетворенность от 1 до 10
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <span className="ms-1 text-muted" style={{ cursor: 'pointer' }}>
                                                <Icon icon={questionIcon} />
                                            </span>
                                        </OverlayTrigger>
                                    </h5>
                                </th>
                                <th><h5 className="text-secondary">Действия</h5></th>
                            </tr>
                        </thead>
                        {sortedRoles.length > 0 && (
                            <tbody>
                                {sortedRoles.map((part, index) => (
                                    <tr
                                        key={part.uuid}
                                        draggable={!readonly}
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDrop={(e) => handleDrop(e, index)}
                                        style={{ cursor: readonly ? 'default' : 'grab' }}
                                    >
                                        <td style={{ maxWidth: 36, minWidth: 36 }}>
                                            <h6>
                                                {index + 1}
                                            </h6>
                                        </td>
                                        <td>
                                            <h6 style={{ cursor: 'pointer' }} onClick={() => navigate(`/${wheelId}/roles/${part.uuid}`)}>
                                                <strong>{part.name}</strong>
                                            </h6>
                                            <LifePartIndicator
                                                lifePart={part}
                                                tasks={tasks.objects}
                                                plans={plans.objects}
                                                trackers={trackers.objects}
                                            />
                                        </td>
                                        <td><ColorPicker value={part.color || '#000000'} onClick={e => e.stopPropagation()} onChange={(color) => handleColorChange(part, color)} disabled={readonly} /></td>
                                        <td>
                                            <ButtonGroup size="sm">
                                                <Button
                                                    onClick={(e) => { handleRatingChange(part, -1); e.stopPropagation() }}
                                                    variant="outline-secondary"
                                                    disabled={readonly || (part.startpoint || 1) <= 1}
                                                >
                                                    <Icon icon={minus} />
                                                </Button>
                                                <span className="d-inline-flex align-items-center justify-content-center px-2" style={{ minWidth: 32 }}>
                                                    {part.startpoint || 1}
                                                </span>
                                                <Button
                                                    onClick={(e) => { handleRatingChange(part, 1); e.stopPropagation() }}
                                                    variant="outline-secondary"
                                                    disabled={readonly || (part.startpoint || 0) >= 10}
                                                >
                                                    <Icon icon={plus} />
                                                </Button>
                                            </ButtonGroup>
                                        </td>
                                        <td>
                                            <ButtonGroup size="sm" className="me-4">
                                                <Button onClick={(e) => { handleMoveUp(part); e.stopPropagation() }} variant="outline-secondary" disabled={readonly}>
                                                    <Icon icon={arrowUp} />
                                                </Button>
                                                <Button onClick={(e) => { handleMoveDown(part); e.stopPropagation() }} variant="outline-secondary" disabled={readonly}>
                                                    <Icon icon={arrowDown} />
                                                </Button>
                                            </ButtonGroup>
                                            <Button className="mr-3" size="sm" variant="danger" disabled={readonly} onClick={(e) => { e.stopPropagation(); handleDeleteLifePart(part) }}>
                                                <Icon icon={trash}></Icon>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        )}
                    </Table>
                    }
                    {sortedRoles.length > 12 && <Alert variant="warning"><strong>Оптимальное количество частей: от 6 до 12</strong></Alert>}

                    {newLifePart && <LifePartSimpleModal toggle={toggleNew} popup={newLifePart} />}
                    <Button size="sm" variant="outline-success" disabled={readonly} onClick={handleOpenNewLifePartModal}>
                        <Icon icon={add}></Icon>
                        &nbsp;Добавить
                    </Button>
                </Col>
                <Col lg={5}>
                    <Chart
                        allMaxValues={!Boolean(hasPoints)}
                        showLabels={false}
                        onClick={(index) => navigate(`/${wheelId}/roles/${sortedRoles[index].uuid}`)}
                        roles={sortedRoles.filter((p): p is LifePart => p !== undefined)}
                    />

                </Col>
            </>
        )}
    </Row>

    );
}
